<script>
  import { user, setSession, logout, userRoles, userReadOnly } from '../stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { toSearchParam, assignmentForProtocol, hasUnrestrictedAccess } from '$utils/userAssignments.js';
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { debounce } from 'lodash-es';
  import { theme, toggleTheme } from '$stores/themeStore';
  import { glucoseunit, toggleGlucoseUnit } from '$stores/unitsStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { config, showFeatureInfo } from '$stores/configStore';
  import { studyProperties } from '$stores/configStore';
  import { isCompactMode } from '$stores/uiStore';
  import LanguageSelector from './LanguageSelector.svelte';
  import DataManagementModal from './DataManagementModal.svelte';
  import {
    faUser,
    faMoon,
    faSun,
    faSignOut,
    faUserCog,
    faUserCircle,
    faFileLines,
    faCalendarDay,
    faMessage,
    faGear,
    faChevronUp,
    faChevronDown,
    faLanguage,
    faUserGroup,
    faMicrochip,
    faMobileScreen,
    faBook,
    faInfoCircle,
    faClock,
    faClockRotateLeft,
    faList,
    faDatabase,
  } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '../stores/selectionStore';
  import { currentDatabase, fetchCurrentDatabase, getAppTitle } from '../stores/databaseStore';
  // Using local API endpoints for authentication
  const AUTH_BASE_URL = '/api/proxy';
  const API_PROXY_BASE_URL = '/api/proxy';

  // Get app title - reactive based on current database
  $: APP_TITLE = getAppTitle($currentDatabase);

  $: showHeaderAdminMenus = hasAccessUiAction(
    'shell.header.adminMenus',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  $: canOpenStudyUsers = $user?.canManageUsers === true
    && ($userRoles || []).includes('study')
    && !($userRoles || []).includes('admin')
    && !!assignmentForProtocol($user, $selectedStudy?.protocolNumber);

  $: showHeaderDocumentationMenu = hasAccessUiAction(
    'shell.header.documentationMenu',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  $: canOpenApiDocumentation = hasAccessUiAction(
    'apidocs.access',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Extend when more documentation dropdown rows exist (each with its own access key).
  $: hasVisibleDocumentationMenuItems = canOpenApiDocumentation;

  //export let onLogin;
  //export let onLogout;

  let searchText = '';
  let searchResults = [];
  let showDropdown = false;
  let searchInput;
  let showProfileMenu = false;
  let showSettingsMenu = false;
  let showDocumentationMenu = false;
  let showDataManagementModal = false;

  // Debounced search function to prevent too many API calls
  const debouncedSearch = debounce(async (text) => {
    if (text.length < 2) {
      searchResults = [];
      return;
    }

    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '99',
        searchText: text,
        sortOrder: 'asc'
      });

      if ($selectedStudy?._id) {
        params.set('studyGUID', $selectedStudy._id);
      }

      const assignmentParam = toSearchParam($user);
      if (!assignmentParam && $user && !hasUnrestrictedAccess($user)) {
        searchResults = [];
        return;
      }
      if (assignmentParam) {
        params.set('assignments', assignmentParam);
      }

      const searchUrl = `${API_PROXY_BASE_URL}/search/subject?${params.toString()}`;

      const response = await fetch(searchUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      searchResults = data.data || [];
    } catch (error) {
      console.error('Search error:', error);
      searchResults = [];
    }
  }, 50);

  // Handle search input changes
  function handleSearchInput(event) {
    console.log('Search input event triggered:', event.target.value);
    console.log('User logged in:', !!$user);

    if (!$user) return; // Don't perform search if user is not logged in

    searchText = event.target.value;
    showDropdown = true;
    console.log('Calling debouncedSearch with:', searchText);
    debouncedSearch(searchText);
  }

  // Handle clicking outside of search component
  function handleClickOutside(event) {
    if (searchInput && !searchInput.contains(event.target)) {
      showDropdown = false;
    }
    if (showProfileMenu && !event.target.closest('.profile-menu')) {
      showProfileMenu = false;
    }
    if (showSettingsMenu && !event.target.closest('.settings-menu')) {
      showSettingsMenu = false;
    }
    if (showDocumentationMenu && !event.target.closest('.documentation-menu')) {
      showDocumentationMenu = false;
    }
  }

  const handlePersonClick = async (studyGuid, siteGuid, participantGuid) => {
    searchText = '';
    showDropdown = false;

    // Find complete objects using GUIDs
    const study = await fetch(`${API_PROXY_BASE_URL}/studies/${studyGuid}`).then((res) => res.json());

    //console.log('Study: ', study.protocolNumber);

    const site = await fetch(`${API_PROXY_BASE_URL}/sites/${siteGuid}`).then((res) => res.json());

    //console.log('Site: ', site);

    const participant = await fetch(`${API_PROXY_BASE_URL}/persons/${participantGuid}`).then((res) =>
      res.json()
    );

    //console.log('Subject: ', participant);

    if (!study || !site || !participant) {
      console.error('Could not find complete objects for selection');
      return;
    }

    // Set complete objects in stores
    selectedStudy.set(study);
    selectedSite.set(site);
    selectedParticipant.set(participant);

    goto(`/person`);
  };

  onMount(() => {
    // Handle click outside
    document.addEventListener('click', handleClickOutside);

    // Fetch current database info for title display
    fetchCurrentDatabase();

    // Handle auth callback, but only if NOT on the dashboard page
    // Dashboard page handles this itself, and we don't want both to process at the same time
    const urlParams = new URLSearchParams(window.location.search);
    const userDataParam = urlParams.get('userData');
    const isOnDashboard = window.location.pathname === '/dashboard';

    if (userDataParam && !isOnDashboard) {
      try {
        const userData = JSON.parse(decodeURIComponent(userDataParam));
        console.log('Header parsed user data:', userData);
        setSession(userData);
        // Clear the URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    // Cleanup function
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Add debug logging
  // $: {
  //   console.log('User data:', $user);
  //   console.log('Organizations:', $user?.customData?.organizations);
  //   console.log('Primary Org ID:', $user?.customData?.primaryOrganizationId);
  // }

  // Get user and organization data
  // Role display mapping
  const roleDisplayNames = {
    'admin': 'Administrator',
    'read-only-b': 'Read Only Blinded',
    'read-only-u': 'Read Only Unblinded',
    'site': 'Site User',
    'study': 'Study User',
    'support': 'Support User'
  };
  
  $: roleDisplay = roleDisplayNames[$user?.customData?.roles?.[0]] || $user?.customData?.roles?.[0] || 'Standard user';
  $: organizations = $user?.customData?.organizations || [];
  $: primaryOrganizationId = $user?.customData?.primaryOrganizationId;
  $: currentOrg = organizations.find((org) => org.organizationId === primaryOrganizationId);

  // Computed properties to check if user has assigned studies/sites
  $: unrestricted = hasUnrestrictedAccess($user);

  // Format role for display (capitalize first letter)
  // $: roleDisplay = currentOrg?.role
  //   ? currentOrg.role.charAt(0).toUpperCase() + currentOrg.role.slice(1)
  //   : '';

  function handleKeydown(event) {
    if (!$user) return; // Don't handle keydown if user is not logged in
    if (event.key === 'Enter' && searchResults && searchResults.length > 0) {
      const firstResult = searchResults[0];
      handlePersonClick(firstResult.studyGUID, firstResult.siteGUID, firstResult.subjectGUID);
    }
  }

  // Add this subscription to see when the user store updates
  // user.subscribe(value => {
  //   console.log('User store updated:', value);
  // });

  function handleProfileClick(event) {
    event.stopPropagation();
    showProfileMenu = !showProfileMenu;
    showSettingsMenu = false;
    showDocumentationMenu = false;
  }

  function handleSettingsClick(event) {
    event.stopPropagation();
    showSettingsMenu = !showSettingsMenu;
    showProfileMenu = false;
    showDocumentationMenu = false;
  }

  function handleDocumentationMenuClick(event) {
    event.stopPropagation();
    showDocumentationMenu = !showDocumentationMenu;
    showProfileMenu = false;
    showSettingsMenu = false;
  }

  import { isHeaderVisible, toggleBreadcrumbs } from '$stores/uiStore';
  import { tr, le, tui } from '$stores/translationStore';
  
  // Workflows feature flag derived from study properties
  $: isWorkflowsEnabled = !!$studyProperties?.features?.workflows;
</script>

<header
  class="flex items-center p-2 bg-gray-800 dark:bg-gray-800 text-white text-xl"
  style="position: fixed; top: 0; left: 0; right: 0; z-index: 60;"
>
  <!-- Left section with fixed width -->
  {#if $user && unrestricted}
    <button type="button" class="flex items-center text-s w-100" on:click={() => goto('/studies')}>
      <img src="/summa-logo-big.png" alt="Logo" class="h-7 mr-3" />
      <span>{APP_TITLE}</span>
    </button>
  {:else}
    <button type="button" class="flex items-center text-s w-100" on:click={() => goto('/')}>
      <img src="/summa-logo-big.png" alt="Logo" class="h-7 mr-3" />
      <span>{APP_TITLE}</span>
    </button>
  {/if}

  <!-- Center section with search - using absolute positioning -->
  <div class="absolute left-1/2 transform -translate-x-1/2 z-50 {$isCompactMode ? 'w-60' : 'w-80'}">
    <div class="relative" bind:this={searchInput}>
      <input
        type="text"
        bind:value={searchText}
        on:input={handleSearchInput}
        on:keydown={handleKeydown}
        placeholder={$user
          ? tui($tr, $le, 'header.searchParticipants', 'Find participants ...')
          : tui($tr, $le, 'header.pleaseLogin', 'Please login to search')}
        disabled={!$user}
        class="w-full px-3 py-1.5 text-xs text-white-900 dark:text-white bg-gray-700 dark:bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 {!$user
          ? 'cursor-not-allowed opacity-50'
          : ''}"
      />

      {#if showDropdown && searchResults.length > 0 && $user}
        <div
          class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {#each searchResults as result}
            <button
              type="button"
              class="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700"
              on:click={() =>
                handlePersonClick(result.studyGUID, result.siteGUID, result.subjectGUID)}
            >
              <div class="font-medium">{result.subjectId}</div>
              <div class="text-xs text-gray-600 dark:text-gray-400">
                {tui($tr, $le, 'header.protocol', 'Protocol')}: {result.protocolNumber} • {tui(
                  $tr,
                  $le,
                  'header.site',
                  'Site'
                )}: {result.siteId}
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  <!-- Right section with fixed width -->
  <div class="ml-auto w-100 text-right pr-4">
    {#if $user}
      <div class="flex items-center space-x-4">
        {#if showHeaderAdminMenus}
          <LanguageSelector />
        {/if}
        {#if showHeaderDocumentationMenu && hasVisibleDocumentationMenuItems}
          <div class="relative documentation-menu">
            <button
              type="button"
              on:click={handleDocumentationMenuClick}
              class="flex items-center space-x-2 text-sm hover:text-blue-300 transition-colors"
              title={tui($tr, $le, 'header.documentationMenu', 'Documentation')}
            >
              <FontAwesomeIcon icon={faBook} class="h-5 w-5" />
            </button>

            {#if showDocumentationMenu}
              <div
                class="documentation-menu absolute right-0 mt-2 min-w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50"
              >
                <button
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/apidocs');
                    showDocumentationMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.apiDocs', 'API documentation')}</span>
                  <FontAwesomeIcon icon={faBook} class="h-4 w-4" />
                </button>
              </div>
            {/if}
          </div>
        {/if}
        {#if showHeaderAdminMenus || canOpenStudyUsers}
          <div class="relative settings-menu">
            <button
              type="button"
              on:click={handleSettingsClick}
              class="flex items-center space-x-2 text-sm hover:text-blue-300 transition-colors"
              title={tui($tr, $le, 'header.adminMenu', 'Administration')}
            >
              <FontAwesomeIcon icon={faGear} class="h-5 w-5" />
            </button>

            {#if showSettingsMenu}
              <div
                class="settings-menu absolute right-0 mt-2 min-w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50"
              >
                {#if !showHeaderAdminMenus}
                  <button
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    on:click={() => {
                      goto('/users');
                      showSettingsMenu = false;
                    }}
                  >
                    <span>{tui($tr, $le, 'header.users', 'Users')}</span>
                    <FontAwesomeIcon icon={faUser} class="h-4 w-4" />
                  </button>
                {:else}
                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/devices');
                    showSettingsMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.devices', 'Devices')}</span>
                  <FontAwesomeIcon icon={faMicrochip} class="h-4 w-4" />
                </button>

                <!-- <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/phoneInventory');
                    showSettingsMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.phoneInventory', 'Phone inventory')}</span>
                  <FontAwesomeIcon icon={faMobileScreen} class="h-4 w-4" />
                </button> -->

                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/lookupManagement');
                    showSettingsMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.lookupManagement', 'Lookup Values')}</span>
                  <FontAwesomeIcon icon={faList} class="h-4 w-4" />
                </button>

                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/languages');
                    showSettingsMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.languages', 'Languages')}</span>
                  <FontAwesomeIcon icon={faMessage} class="h-4 w-4" />
                </button>

                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/localization');
                    showSettingsMenu = false;
                  }}
                >
                  <span>{tui($tr, $le, 'header.localization', 'Localization')}</span>
                  <FontAwesomeIcon icon={faLanguage} class="h-4 w-4" />
                </button>

                {#if showHeaderAdminMenus}
                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/regulatorySettings');
                    showSettingsMenu = false;
                  }}
                >
                  <span>Regulatory</span>
                  <FontAwesomeIcon icon={faFileLines} class="h-4 w-4" />
                </button>
                {/if}

                <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>

                <button
                  class="w-full text-left px-4 py-2 text-sm flex items-center justify-between {$selectedStudy
                    ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'}"
                  on:click={() => {
                    if ($selectedStudy) {
                      goto('/forms');
                      showSettingsMenu = false;
                    }
                  }}
                  disabled={!$selectedStudy}
                >
                  <span>{tui($tr, $le, 'header.diaryForms', 'Forms')}</span>
                  <FontAwesomeIcon icon={faFileLines} class="h-4 w-4" />
                </button>

                <button
                  class="w-full text-left px-4 py-2 text-sm flex items-center justify-between {$selectedStudy
                    ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'}"
                  on:click={() => {
                    if ($selectedStudy) {
                      goto('/studySchedules');
                      showSettingsMenu = false;
                    }
                  }}
                  disabled={!$selectedStudy}
                >
                  <span>{tui($tr, $le, 'header.schedules', 'Schedules')}</span>
                  <FontAwesomeIcon icon={faCalendarDay} class="h-4 w-4" />
                </button>

                {#if showHeaderAdminMenus}
                  {#if isWorkflowsEnabled}
                  <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>

                  <button
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    on:click={() => {
                      goto('/workflows');
                      showSettingsMenu = false;
                    }}
                  >
                    <span>Workflows</span>
                    <FontAwesomeIcon icon={faList} class="h-4 w-4" />
                  </button>
                  {/if}

                  <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>
                  
                  <button
                    class="w-full text-left px-4 py-2 text-sm flex items-center justify-between {$selectedStudy
                      ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                      : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'}"
                    on:click={() => {
                      if ($selectedStudy) {
                        goto('/schedulerJobs');
                        showSettingsMenu = false;
                      }
                    }}
                    disabled={!$selectedStudy}
                  >
                    <span>{tui($tr, $le, 'header.schedulerJobs', 'Job Scheduler')}</span>
                    <FontAwesomeIcon icon={faClockRotateLeft} class="h-4 w-4" />
                  </button>

                  <button
                    class="w-full text-left px-4 py-2 text-sm flex items-center justify-between {$selectedStudy
                      ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                      : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'}"
                    on:click={() => {
                      if ($selectedStudy) {
                        goto('/schedulerLogs');
                        showSettingsMenu = false;
                      }
                    }}
                    disabled={!$selectedStudy}
                  >
                    <span>{tui($tr, $le, 'header.schedulerLogs', 'Job Logs')}</span>
                    <FontAwesomeIcon icon={faClock} class="h-4 w-4" />
                  </button>

                  <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>

                  <button
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    on:click={() => {
                      goto('/users');
                      showSettingsMenu = false;
                    }}
                  >
                    <span>{tui($tr, $le, 'header.users', 'Users')}</span>
                    <FontAwesomeIcon icon={faUser} class="h-4 w-4" />
                  </button>

                  <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>

                  <button
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    on:click={() => {
                      showDataManagementModal = true;
                      showSettingsMenu = false;
                    }}
                  >
                    <span>Data Management</span>
                    <FontAwesomeIcon icon={faDatabase} class="h-4 w-4" />
                  </button>

                  <!-- <button
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    on:click={() => {
                      goto('/roles');
                      showSettingsMenu = false;
                    }}
                  >
                    <span>{tui($tr, $le, 'header.roles', 'Roles')}</span>
                    <FontAwesomeIcon icon={faUserGroup} class="h-4 w-4" />
                  </button> -->

                {/if}
                {/if}
              </div>
            {/if}
          </div>
        {/if}

        <div class="relative profile-menu">
          <button
            on:click={handleProfileClick}
            class="flex items-center space-x-2 text-sm hover:text-blue-300 transition-colors"
          >
            {#if $user?.picture}
              <img src={$user.picture} alt="Profile" class="h-6 w-6 rounded-full" />
            {:else}
              <FontAwesomeIcon icon={faUserCircle} class="h-5 w-5" />
            {/if}
          </button>

          {#if showProfileMenu}
            <div
              class="absolute right-0 mt-2 min-w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50"
            >
              <div class="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {$user.email}
                </div>
                {#if $user?.customData?.roles && $user.customData.roles.length > 0}
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {roleDisplay}
                  </div>
                {/if}
              </div>

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click|preventDefault={() => {
                  goto('/dashboard');
                  showProfileMenu = false;
                }}
              >
                <span>{tui($tr, $le, 'header.userProfile', 'User Profile')}</span>
                <FontAwesomeIcon icon={faUserCog} class="h-4 w-4" />
              </button>

              {#if isWorkflowsEnabled}
                <button
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  on:click={() => {
                    goto('/myWorkflows');
                    showProfileMenu = false;
                  }}
                >
                  <span>My Workflows</span>
                  <FontAwesomeIcon icon={faList} class="h-4 w-4" />
                </button>
              {/if}

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click={() => {
                  toggleTheme();
                  showProfileMenu = false;
                }}
              >
                <span>{tui($tr, $le, 'header.theme', 'Theme')}</span>
                <FontAwesomeIcon icon={$theme === 'dark' ? faMoon : faSun} class="h-4 w-4" />
              </button>

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click={() => {
                  toggleBreadcrumbs();
                  showProfileMenu = false;
                }}
              >
                <span>{tui($tr, $le, 'header.breadcrumbs', 'Compact Mode')}</span>
                <FontAwesomeIcon
                  icon={$isHeaderVisible ? faChevronUp : faChevronDown}
                  class="h-4 w-4"
                />
              </button>

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click={() => {
                  showFeatureInfo.update((v) => !v);
                  showProfileMenu = false;
                }}
              >
                <span>{tui($tr, $le, 'header.featureInfo', 'Feature Info')}</span>
                <FontAwesomeIcon icon={faInfoCircle} class="h-4 w-4" />
              </button>

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click={() => {
                  toggleGlucoseUnit();
                  showProfileMenu = false;
                }}
              >
                <span>{tui($tr, $le, 'header.unit', 'Unit')}</span>
                <span class="flex items-center">
                  {$glucoseunit}
                </span>
              </button>

              <button
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                on:click={() => {
                  logout();
                }}
              >
                <span>{tui($tr, $le, 'header.signOut', 'Sign Out')}</span>
                <span class="flex items-center">
                  <FontAwesomeIcon icon={faSignOut} class="h-4 w-4 mr-1" />
                </span>
              </button>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</header>

<!-- Data Management Modal -->
<DataManagementModal bind:show={showDataManagementModal} />

<style>
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* Custom scrollbar for the dropdown */
  :global(.overflow-y-auto) {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
  }

  :global(.overflow-y-auto::-webkit-scrollbar) {
    width: 6px;
  }

  :global(.overflow-y-auto::-webkit-scrollbar-track) {
    background: #f7fafc;
  }

  :global(.overflow-y-auto::-webkit-scrollbar-thumb) {
    background-color: #cbd5e0;
    border-radius: 3px;
  }
</style>
